import { ICashBreakdownList } from '@modules/front-office/interface/fo-cash-breakdown.interface';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@services/baseQuery';
import moment, { Moment } from 'moment';
import {
  APIInterface,
  APINationality,
  BillingStatementInterface,
  ChargingStationReport,
  GuestFolioResult,
  ICollectionResult,
  ICollectionSummarize,
  IJournalEntries,
  InterfaceRoomEventsHistory,
  InterfaceRoomEventsHistoryAPI,
  ISalesInvoiceIssuedList,
  TransactionData,
  UserDataTransaction,
  VoidedItemsReport,
} from '../../modules/front-office/interface/reports.interface';
import { BookingDetails } from '../../modules/front-office/interface/voucher.interface';
import { handleError } from '../../utils/api-error-handling';

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: baseQuery,
  tagTypes: ['Reports'],
  endpoints: (builder) => ({
    getGuestFolio: builder.query({
      query: (query) => {
        return `reports/folio${query}`;
      },
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: GuestFolioResult) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0.0001,
    }),
    getBookingDetails: builder.query({
      query: (query) => {
        return `reports/folio${query}&showTnC=true&showROC=false`;
      },
      providesTags: ['Reports'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        await queryFulfilled;
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: BookingDetails) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0.0001,
    }),
    getBillingStatement: builder.query({
      query: (params) => `roomposting/guest-billing/${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: {
        data: {
          getGuestBillingData: BillingStatementInterface[];
        };
      }) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getManagersFlashReports: builder.query({
      query: (params) => `reports/flashreport/?filter[salesDate]=${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getRoomEventsHistory: builder.query({
      query: (params) =>
        `reports/roomeventshistory/?filter[dateFrom]=${params.dateFrom}&filter[dateTo]=${params.dateTo}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: InterfaceRoomEventsHistoryAPI) => {
        return response.data.roomEventsHistory.reduce(
          (
            accumulator: InterfaceRoomEventsHistory[],
            currentValue: InterfaceRoomEventsHistory,
          ) => {
            if (!accumulator.find((item) => item.id === currentValue.id)) {
              accumulator.push(currentValue);
            }
            return accumulator;
          },
          [],
        );
      },
      forceRefetch() {
        return true;
      },
    }),

    getActualAndForecast: builder.query({
      query: (params) =>
        `reports/actualandforecast/?filter[dateFrom]=${params?.dateFrom}&filter[dateTo]=${params?.dateTo}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getNationalityReport: builder.query({
      query: (params) =>
        `reports/nationality/?filter[dateFrom]=${params?.dateFrom}&filter[dateTo]=${params?.dateTo}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: APINationality) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getGroupForms: builder.query({
      query: (params) =>
        `reports/groupforms/?filter[formType]=${params?.formType}&filter[groupReferenceID]=${params?.groupReferenceID}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getCollectionReport: builder.query({
      query: (params) => `reports/collections${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: ICollectionResult) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getTransactionReport: builder.query({
      query: (params) => `reports/fo-transactions${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: APIInterface<TransactionData>) => {
        type GroupedData = {
          [property: string]: {
            [user: string]: {
              [header: string]: {
                [salesDate: string]: UserDataTransaction[];
              };
            };
          };
        };

        const groupedData: GroupedData = {};

        response.data.transactions?.map(({ transaction }) =>
          transaction?.forEach(({ salesDate, properties }) => {
            properties?.forEach(({ propertyID, users }) => {
              if (!groupedData[propertyID]) {
                groupedData[propertyID] = {};
              }

              if (!groupedData[propertyID][salesDate]) {
                groupedData[propertyID][salesDate] = {};
              }

              users?.forEach(({ user, transactions }) => {
                if (!groupedData[propertyID][salesDate][user]) {
                  groupedData[propertyID][salesDate][user] = {};
                }

                transactions.transaction?.forEach(({ header, data }) => {
                  if (!groupedData[propertyID][salesDate][user][header]) {
                    groupedData[propertyID][salesDate][user][header] = [];
                  }

                  groupedData[propertyID][salesDate][user][header].push(
                    ...data,
                  );
                });
              });
            });
          }),
        );

        return groupedData;
      },
      forceRefetch() {
        return true;
      },
    }),

    getCollectionReportSummarize: builder.query({
      query: (params) => `reports/collections${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: ICollectionSummarize) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getJournalEntriesReport: builder.query({
      query: (params) => `reports/journal${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: IJournalEntries) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getSalesInvoiceIssuedList: builder.query({
      query: (params) => `reports/issued-sales-invoices${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: ISalesInvoiceIssuedList) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getVoidedItemsReport: builder.query({
      query: (params) => `reports/voided-items${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: APIInterface<VoidedItemsReport>) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getChargingStationReport: builder.query({
      query: (params) => `reports/charging-station-report${params}`,
      providesTags: ['Reports'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (
        response: APIInterface<ChargingStationReport>,
      ) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getCashBreakdownReport: builder.query({
      query: ({ dateStart, dateEnd }: { dateStart: Moment; dateEnd: Moment }) =>
        `reports/cash-breakdown?filter[dateStart]=${moment(dateStart).format('YYYY-MM-DD')}&filter[dateEnd]=${moment(dateEnd).format('YYYY-MM-DD')}`,
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (
        response: APIInterface<{
          cashBreakdownReport: ICashBreakdownList[][];
        }>,
      ) => {
        return response.data?.cashBreakdownReport?.[0];
      },
      forceRefetch() {
        return true;
      },
    }),
  }),
});

export const {
  useLazyGetGuestFolioQuery,
  useGetBookingDetailsQuery,
  useLazyGetBookingDetailsQuery,
  useLazyGetBillingStatementQuery,
  useLazyGetManagersFlashReportsQuery,
  useLazyGetRoomEventsHistoryQuery,
  useLazyGetActualAndForecastQuery,
  useLazyGetNationalityReportQuery,
  useLazyGetGroupFormsQuery,
  useLazyGetCollectionReportQuery,
  useLazyGetCollectionReportSummarizeQuery,
  useGetTransactionReportQuery,
  useLazyGetTransactionReportQuery,
  useLazyGetJournalEntriesReportQuery,
  useLazyGetSalesInvoiceIssuedListQuery,
  useLazyGetVoidedItemsReportQuery,
  useLazyGetChargingStationReportQuery,
  useLazyGetCashBreakdownReportQuery,
} = reportsApi;
